import { type Permiso, type Rol, rolSchema, rolTienePermiso } from '@pasos/schemas';

/**
 * Qué puede abrir cada rol · la navegación sale de la matriz (P-66, E3.7).
 *
 * Antes el sidebar le ofrecía los mismos tres destinos a todos: un profesional
 * entraba a Operación y la api le devolvía 403. El backend tenía razón; la
 * navegación ofrecía un lugar que para ese rol no existe. **La salida no es
 * ampliar el permiso: es no ofrecer el destino.**
 *
 * Cada pantalla nombra **el permiso que exige el endpoint que lee** (entre
 * paréntesis), y el permiso se evalúa con la misma matriz de `@pasos/schemas`
 * que usa el backend. Con esta tabla se filtran el sidebar, las pestañas de la
 * ficha y los enlaces sueltos (el "Ver caso" de Operación, el "Notas" de
 * Contexto), y se arman los guards de las rutas: una URL escrita a mano muestra
 * S-10 en vez de una pantalla que falla.
 *
 * Ocultar sigue siendo cortesía: la garantía es el 403 del backend.
 */
export const PERMISO_DE_LA_PANTALLA = Object.freeze({
  /** `GET /api/v1/cohorte` */
  dashboard: 'paciente:ver',
  /** `GET /api/v1/pacientes` */
  pacientes: 'paciente:ver',
  /** `GET /api/v1/operacion` */
  operacion: 'cohorte:ver',
  /** `GET /api/v1/pacientes/:id` y su semáforo; Contexto y Escalas no leen más que eso. */
  ficha: 'paciente:ver',
  /**
   * `GET /api/v1/pacientes/:id/timeline` y, con el filtro "Del paciente",
   * `GET /api/v1/pacientes/:id/interacciones`: los dos piden lo mismo.
   */
  historia: 'timeline:ver',
  /** `GET /api/v1/pacientes/:id/notas` */
  notas: 'nota:registrar',
  /** `POST /api/v1/programas/:id/continuidad` */
  acciones: 'paciente:gestionar',
  /** `POST /api/v1/altas` (E3.24): profesional, coordinación y administración. */
  alta: 'paciente:dar_de_alta',

  // ── La administración de Fundación (ADMIN-1) ──────────────────────────────
  /** `GET /api/v1/fundacion/red` */
  red: 'red:ver',
  /** `GET /api/v1/fundacion/instituciones` y la ficha de una. */
  instituciones: 'red:ver',
  /**
   * Las tres que todavía no leen nada: dicen qué va a haber y en qué etapa.
   * Cada una nombra el permiso de lo que va a ser, para que el menú ya salga de
   * la matriz: la gestión de cuentas (INGRESO-5) cuelga de la red, el corpus de
   * `corpus:gestionar` (E4) y el estado del sistema de `sistema:ver` (E5).
   */
  equipos: 'red:ver',
  contenidos: 'corpus:gestionar',
  sistema: 'sistema:ver',
} satisfies Record<string, Permiso>);

/** Las pantallas de Fundación, aparte de las del equipo clínico. */
export const PANTALLAS_DE_FUNDACION: readonly PantallaDelEquipo[] = Object.freeze([
  'red',
  'instituciones',
  'equipos',
  'contenidos',
  'sistema',
]);

export type PantallaDelEquipo = keyof typeof PERMISO_DE_LA_PANTALLA;

export function puedeVer(rol: Rol | undefined, pantalla: PantallaDelEquipo): boolean {
  return rol !== undefined && rolTienePermiso(rol, PERMISO_DE_LA_PANTALLA[pantalla]);
}

/** Los roles que pueden abrir una pantalla, para el guard de su ruta. */
export function rolesQueVen(...pantallas: PantallaDelEquipo[]): readonly Rol[] {
  return rolSchema.options.filter((rol) => pantallas.some((pantalla) => puedeVer(rol, pantalla)));
}
